import { Timestamp } from "firebase/firestore";
import {
  JobPayment,
  JobV2,
  PaymentHistoryEntry,
  JobLineItem,
} from "../@types/job";
import { TeamMember } from "../@types/team";
import { PaymentStatus, PaymentType } from "../@types/enums";
import {
  calculateJob,
  toDecimal,
  createDecimalValue,
  isEqual,
  isLessThan,
  isGreaterThan,
  isGreaterThanOrEqual,
} from "./math";
import { isServiceLineItem } from "./lineItem";

/**
 * Gets the effective total for a payment (total minus refunds)
 */
export const getEffectiveTotal = (payment: JobPayment): number => {
  const total = toDecimal(payment.total || payment.amount);
  const refunded = toDecimal(payment.totalRefunded || 0);
  return total.minus(refunded).toNumber();
};

/**
 * Gets the refund status of a payment
 */
export const getRefundStatus = (
  payment: JobPayment
): "none" | "partial" | "full" => {
  if (!payment.totalRefunded || payment.totalRefunded === 0) {
    return "none";
  }
  const total = payment.total || payment.amount;
  if (isGreaterThanOrEqual(payment.totalRefunded, total)) {
    return "full";
  }
  return "partial";
};

/**
 * Gets all refund entries from payment history
 */
export const getRefundEntries = (
  payment: JobPayment
): PaymentHistoryEntry[] => {
  if (!payment.history || payment.history.length === 0) {
    return [];
  }
  return payment.history.filter((entry) => entry.action === "refund");
};

/**
 * True when real money moved through Stripe for this payment.
 *
 * Checks the intent id as well as the charge id. The charge id can be
 * backfilled asynchronously by the webhook, so gating on it alone makes edit
 * and delete authorization depend on that backfill having landed. The intent
 * id is written synchronously on every card payment, so it is the safer test.
 */
export const isStripePayment = (payment: JobPayment): boolean =>
  !!(payment.stripeTransactionId || payment.stripePaymentIntentId);

/**
 * Checks if a payment can be edited
 */
export const canEditPayment = (
  payment: JobPayment,
  job: JobV2
): { allowed: boolean; reason?: string } => {
  // One real payment split across several jobs. Editing one portion in
  // isolation would leave the parts no longer adding up to what was taken,
  // and for a card there is a single charge behind all of them.
  if (payment.splitGroupId) {
    return {
      allowed: false,
      reason:
        "This is part of one payment split across several jobs, so it can only be refunded",
    };
  }
  if (isStripePayment(payment)) {
    return {
      allowed: false,
      reason: "A transaction has already occurred and can only be refunded",
    };
  }
  if (isContractPayment(payment, job)) {
    return {
      allowed: false,
      reason: "This payment is part of a payment plan and cannot be modified",
    };
  }
  // Can't edit if there have been any refunds
  if (payment.totalRefunded && payment.totalRefunded > 0) {
    return {
      allowed: false,
      reason: "Payments with refunds cannot be edited",
    };
  }
  return { allowed: true };
};

/**
 * Checks if a payment can be deleted
 */
export const canDeletePayment = (
  payment: JobPayment,
  job: JobV2
): { allowed: boolean; reason?: string } => {
  // See canEditPayment: portions of a split are only reversible by refund.
  if (payment.splitGroupId) {
    return {
      allowed: false,
      reason:
        "This is part of one payment split across several jobs, so it can only be refunded",
    };
  }
  if (isStripePayment(payment)) {
    return {
      allowed: false,
      reason: "A transaction has already occurred and can only be refunded",
    };
  }
  if (isContractPayment(payment, job)) {
    return {
      allowed: false,
      reason: "This payment is part of a payment plan and cannot be modified",
    };
  }
  // Can't delete if there have been any refunds
  if (payment.totalRefunded && payment.totalRefunded > 0) {
    return {
      allowed: false,
      reason: "Payments with refunds cannot be deleted",
    };
  }
  return { allowed: true };
};

/**
 * Checks if a payment can be refunded
 */
export const canRefundPayment = (
  payment: JobPayment,
  job: JobV2
): { allowed: boolean; reason?: string } => {
  if (getRefundStatus(payment) === "full") {
    return {
      allowed: false,
      reason: "Payment has already been fully refunded",
    };
  }
  if (isContractPayment(payment, job)) {
    return {
      allowed: false,
      reason: "This payment is part of a payment plan and cannot be modified",
    };
  }
  return { allowed: true };
};

/**
 * Checks if a payment is part of a contract/payment schedule
 */
export const isContractPayment = (payment: JobPayment, job: JobV2): boolean => {
  if (!job.contract?.paymentSchedule) {
    return false;
  }
  // Explicit link via contractPaymentNumber
  if (payment.contractPaymentNumber !== undefined) {
    return job.contract.paymentSchedule.some(
      (scheduleItem) =>
        scheduleItem.paymentNumber === payment.contractPaymentNumber
    );
  }
  // Fallback: match by stripePaymentIntentId (only when it actually exists)
  if (payment.stripePaymentIntentId) {
    return job.contract.paymentSchedule.some(
      (scheduleItem) =>
        scheduleItem.stripePaymentIntentId === payment.stripePaymentIntentId
    );
  }
  // Backwards compat: if schedule[0] is completed and this is the first payment
  // on the job, it's the initial contract payment (cash/check with no stripe ID)
  const firstScheduleItem = job.contract.paymentSchedule[0];
  if (
    firstScheduleItem?.status === "completed" &&
    job.payments.length > 0 &&
    job.payments[0].uid === payment.uid
  ) {
    return true;
  }
  return false;
};

/**
 * Checks if a payment is a QB invoice deposit (non-Mesquite edge case)
 */
export const isQbInvoiceDeposit = (
  payment: JobPayment,
  job: JobV2
): boolean => {
  if (payment.qbPaymentId || !job.qbInvoiceId) {
    return false;
  }
  // updateJobV2 writes a job's FIRST payment onto the invoice's Deposit field
  // instead of creating a QB Payment, when that payment is typed Deposit or
  // Full and the account is not Mesquite (Mesquite gets a real QB payment, so
  // it is already excluded by the qbPaymentId check above).
  //
  // Full matters as much as Deposit here: processJobPayment rewrites a
  // payment's type to Full whenever it zeroes the balance, so paying a job off
  // in one go lands on this path too. Matching only "Deposit" left those
  // refunds and deletes silently skipping QuickBooks, leaving the invoice
  // showing a deposit that no longer exists.
  const isFirstPayment = job.payments?.[0]?.uid === payment.uid;
  return (
    isFirstPayment &&
    (payment.type === PaymentType.Deposit || payment.type === PaymentType.Full)
  );
};

/**
 * Calculates the payment status based on payments, totals, and remaining balance
 * This mirrors the logic in processJobPayment.ts but works on updated job state
 */
export const calculatePaymentStatus = (
  payments: JobPayment[],
  totalDeposit: number,
  grandTotal: number,
  remainingBalance: number
): PaymentStatus => {
  // Filter out deleted payments for calculations
  const activePayments = payments.filter((p) => !p.deleted);

  // If no active payments, status depends on whether deposits are required
  if (activePayments.length === 0) {
    if (isGreaterThanOrEqual(totalDeposit, 0) && totalDeposit > 0) {
      return PaymentStatus.AwaitingDeposit;
    }
    if (isEqual(grandTotal, 0)) {
      return PaymentStatus.PaidInFull;
    }
    return PaymentStatus.Unpaid;
  }

  // Calculate total of active payments (accounting for refunds)
  const totalPaid = activePayments.reduce((sum, payment) => {
    const effective = getEffectiveTotal(payment);
    return toDecimal(sum).plus(effective).toNumber();
  }, 0);

  // If all payments have been fully refunded, treat as if there are no payments
  if (isEqual(totalPaid, 0)) {
    if (totalDeposit > 0) {
      return PaymentStatus.AwaitingDeposit;
    }
    if (isEqual(grandTotal, 0)) {
      return PaymentStatus.PaidInFull;
    }
    return PaymentStatus.Unpaid;
  }

  // Case 1: Remaining balance is 0 or less - Paid in Full
  if (isEqual(remainingBalance, 0) || isLessThan(remainingBalance, 0)) {
    return PaymentStatus.PaidInFull;
  }

  // Case 2: Has any partial payment - this takes precedence over deposit status
  const hasEffectivePartialPayment = activePayments.some(
    (p) =>
      p.type === PaymentType.Partial && isGreaterThan(getEffectiveTotal(p), 0)
  );
  if (hasEffectivePartialPayment) {
    return PaymentStatus.PartiallyPaid;
  }

  // Case 3: Check if this is a deposit scenario
  // Must have an effective deposit payment (not fully refunded)
  const hasEffectiveDepositPayment = activePayments.some(
    (p) =>
      p.type === PaymentType.Deposit && isGreaterThan(getEffectiveTotal(p), 0)
  );
  // A deposit that was partially refunded should still be "Deposit Received"
  // (the deposit was received — the refund doesn't change that)
  const hasPartiallyRefundedDeposit = activePayments.some(
    (p) =>
      p.type === PaymentType.Deposit &&
      isGreaterThan(p.totalRefunded || 0, 0) &&
      isGreaterThan(getEffectiveTotal(p), 0)
  );
  if (
    hasEffectiveDepositPayment &&
    isGreaterThan(totalDeposit, 0) &&
    (isGreaterThanOrEqual(totalPaid, totalDeposit) ||
      hasPartiallyRefundedDeposit)
  ) {
    return PaymentStatus.DepositReceived;
  }

  // Case 4: Paid something but not fully - partial payment
  if (isLessThan(totalPaid, grandTotal)) {
    return PaymentStatus.PartiallyPaid;
  }

  // Default fallback
  return PaymentStatus.PartiallyPaid;
};

/**
 * Finds the processing fee line item linked to a payment
 */
export const findLinkedProcessingFeeLineItem = (
  payment: JobPayment,
  lineItems: JobLineItem[]
): JobLineItem | undefined => {
  return lineItems.find((item) => {
    if (!isServiceLineItem(item)) return false;
    if (!item.notes) return false;
    try {
      const notes = JSON.parse(item.notes);
      return (
        notes.type === "ON_PAYMENT" && notes.linkedPaymentId === payment.uid
      );
    } catch {
      return false;
    }
  });
};

/**
 * Creates a history entry for a payment action
 * @param timestamp - Firestore Timestamp (pass from caller to ensure correct SDK is used)
 */
export const createPaymentHistoryEntry = (
  action: PaymentHistoryEntry["action"],
  teamMember: TeamMember,
  details: PaymentHistoryEntry["details"],
  timestamp?: any // Accept any Timestamp to avoid client/admin SDK mismatch
): PaymentHistoryEntry => {
  return {
    action,
    timestamp: timestamp || Timestamp.now(),
    teamMember,
    details,
  };
};

interface UpdatePaymentParams {
  job: JobV2;
  paymentUid: string;
  updates: {
    amount?: number;
    date?: any; // Accept any Timestamp to avoid client/admin SDK mismatch
    notes?: string;
    type?: JobPayment["type"];
    tender?: string;
  };
  teamMember: TeamMember;
  timestamp?: any; // Firestore Timestamp from caller (admin or client SDK)
}

interface UpdatePaymentResult {
  success: boolean;
  job?: JobV2;
  paymentStatus?: PaymentStatus;
  error?: string;
}

/**
 * Updates a payment and recalculates job totals
 */
export const updatePayment = ({
  job,
  paymentUid,
  updates,
  teamMember,
  timestamp,
}: UpdatePaymentParams): UpdatePaymentResult => {
  const paymentIndex = job.payments.findIndex((p) => p.uid === paymentUid);
  if (paymentIndex === -1) {
    return { success: false, error: "Payment not found" };
  }

  const payment = job.payments[paymentIndex];
  const canEdit = canEditPayment(payment, job);
  if (!canEdit.allowed) {
    return { success: false, error: canEdit.reason };
  }

  // Track changes for history
  const changes: { field: string; before: any; after: any }[] = [];

  if (updates.amount !== undefined && updates.amount !== payment.amount) {
    changes.push({
      field: "amount",
      before: payment.amount,
      after: updates.amount,
    });
  }
  if (updates.date !== undefined) {
    // Compare timestamps by converting to milliseconds
    const beforeMs = payment.date instanceof Date ? payment.date.getTime() : payment.date?.toMillis?.() ?? 0;
    const afterMs = updates.date instanceof Date ? updates.date.getTime() : updates.date?.toMillis?.() ?? 0;
    if (beforeMs !== afterMs) {
      changes.push({
        field: "date",
        before: payment.date,
        after: updates.date,
      });
    }
  }
  if (updates.notes !== undefined && updates.notes !== payment.notes) {
    changes.push({
      field: "notes",
      before: payment.notes,
      after: updates.notes,
    });
  }
  if (updates.type !== undefined && updates.type !== payment.type) {
    changes.push({ field: "type", before: payment.type, after: updates.type });
  }
  if (updates.tender !== undefined && updates.tender !== payment.tender) {
    changes.push({
      field: "tender",
      before: payment.tender,
      after: updates.tender,
    });
  }

  if (changes.length === 0) {
    return { success: true, job }; // No changes
  }

  // Create updated payment with history entry
  const historyEntry = createPaymentHistoryEntry(
    "update",
    teamMember,
    { changes },
    timestamp
  );

  // Filter out undefined values to avoid Firestore errors
  const definedUpdates: Partial<JobPayment> = {};
  if (updates.amount !== undefined) definedUpdates.amount = updates.amount;
  if (updates.date !== undefined) definedUpdates.date = updates.date;
  if (updates.notes !== undefined) definedUpdates.notes = updates.notes;
  if (updates.type !== undefined) definedUpdates.type = updates.type;
  if (updates.tender !== undefined) definedUpdates.tender = updates.tender;

  // Recalculate total if amount changed (total = amount + processingFee + tax)
  const newAmount =
    updates.amount !== undefined ? updates.amount : payment.amount;
  const newTotal = toDecimal(newAmount)
    .plus(toDecimal(payment.processingFee || 0))
    .plus(toDecimal(payment.tax || 0))
    .toNumber();

  const updatedPayment: JobPayment = {
    ...payment,
    ...definedUpdates,
    total: newTotal,
    history: [...(payment.history || []), historyEntry],
  };

  // Update payments array
  const updatedPayments = [...job.payments];
  updatedPayments[paymentIndex] = updatedPayment;

  // Recalculate job totals
  const remainingBalance = calculateJob
    .remainingBalance(
      job.lineItems,
      job.taxRate,
      updatedPayments,
      job.includeTax
    )
    .raw();

  // Recalculate payment status based on updated payments
  const newPaymentStatus = calculatePaymentStatus(
    updatedPayments,
    job.totalDeposit,
    job.grandTotal,
    remainingBalance
  );

  const updatedJob: JobV2 = {
    ...job,
    payments: updatedPayments,
    remainingBalance,
    paymentStatus: newPaymentStatus,
  };

  return { success: true, job: updatedJob, paymentStatus: newPaymentStatus };
};

interface DeletePaymentParams {
  job: JobV2;
  paymentUid: string;
}

interface DeletePaymentResult {
  success: boolean;
  job?: JobV2;
  paymentStatus?: PaymentStatus;
  error?: string;
}

/**
 * Hard deletes a payment and removes linked processing fee line item
 */
export const deletePayment = ({
  job,
  paymentUid,
}: DeletePaymentParams): DeletePaymentResult => {
  const paymentIndex = job.payments.findIndex((p) => p.uid === paymentUid);
  if (paymentIndex === -1) {
    return { success: false, error: "Payment not found" };
  }

  const payment = job.payments[paymentIndex];
  const canDelete = canDeletePayment(payment, job);
  if (!canDelete.allowed) {
    return { success: false, error: canDelete.reason };
  }

  // Hard delete - remove payment from array
  const updatedPayments = job.payments.filter((p) => p.uid !== paymentUid);

  // Remove linked processing fee line item
  let updatedLineItems = [...job.lineItems];
  const linkedFeeItem = findLinkedProcessingFeeLineItem(payment, job.lineItems);
  if (linkedFeeItem) {
    updatedLineItems = updatedLineItems.filter(
      (item) => item.uid !== linkedFeeItem.uid
    );
  }

  // Recalculate job totals
  const calculations = {
    subtotal: calculateJob.subtotal(updatedLineItems).raw(),
    totalDiscount: calculateJob.totalDiscount(updatedLineItems).raw(),
    subtotalAfterDiscounts: calculateJob
      .subtotalAfterDiscounts(updatedLineItems)
      .raw(),
    totalTax: calculateJob
      .tax(updatedLineItems, job.taxRate, job.includeTax)
      .raw(),
    grandTotal: calculateJob
      .grandTotal(updatedLineItems, job.taxRate, job.includeTax)
      .raw(),
    totalDeposit: calculateJob.totalDeposit(updatedLineItems).raw(),
    remainingBalance: calculateJob
      .remainingBalance(
        updatedLineItems,
        job.taxRate,
        updatedPayments,
        job.includeTax
      )
      .raw(),
  };

  // Recalculate payment status based on updated payments
  const newPaymentStatus = calculatePaymentStatus(
    updatedPayments,
    calculations.totalDeposit,
    calculations.grandTotal,
    calculations.remainingBalance
  );

  const updatedJob: JobV2 = {
    ...job,
    ...calculations,
    payments: updatedPayments,
    lineItems: updatedLineItems,
    paymentStatus: newPaymentStatus,
  };

  return { success: true, job: updatedJob, paymentStatus: newPaymentStatus };
};

interface RefundPaymentParams {
  job: JobV2;
  paymentUid: string;
  refundAmount: number;
  reason?: string;
  teamMember: TeamMember;
  stripeRefundId?: string;
  timestamp?: any; // Firestore Timestamp from caller (admin or client SDK)
  // Cross-currency: when the parent payment was charged in a different
  // currency than the job, these record what Stripe actually refunded.
  refundProcessedAmount?: number;
  refundProcessedCurrency?: string;
  refundFxRate?: number;
}

interface RefundPaymentResult {
  success: boolean;
  job?: JobV2;
  paymentStatus?: PaymentStatus;
  error?: string;
}

/**
 * Refunds a payment (partial or full) and prorates processing fee line item
 */
export const refundPayment = ({
  job,
  paymentUid,
  refundAmount,
  reason,
  teamMember,
  stripeRefundId,
  timestamp,
  refundProcessedAmount,
  refundProcessedCurrency,
  refundFxRate,
}: RefundPaymentParams): RefundPaymentResult => {
  const paymentIndex = job.payments.findIndex((p) => p.uid === paymentUid);
  if (paymentIndex === -1) {
    return { success: false, error: "Payment not found" };
  }

  const payment = job.payments[paymentIndex];
  const canRefund = canRefundPayment(payment, job);
  if (!canRefund.allowed) {
    return { success: false, error: canRefund.reason };
  }

  // Validate refund amount. Round to 2dp so the comparison matches the
  // currency-formatted value the user sees (avoids "491.93 > 491.925"
  // when payment.total has trailing precision from processing-fee math).
  const effectiveTotal = Number(getEffectiveTotal(payment).toFixed(2));
  if (refundAmount <= 0) {
    return { success: false, error: "Refund amount must be greater than 0" };
  }
  if (refundAmount > effectiveTotal) {
    return {
      success: false,
      error: `Refund amount cannot exceed remaining balance of ${effectiveTotal}`,
    };
  }

  // Calculate new total refunded (rounded to 2dp to match storage precision)
  const newTotalRefunded = toDecimal(payment.totalRefunded || 0)
    .plus(refundAmount)
    .toDecimalPlaces(2)
    .toNumber();

  // Create history entry - only include defined values to avoid Firestore errors
  const refundDetails: PaymentHistoryEntry["details"] = { refundAmount };
  if (reason) refundDetails.reason = reason;
  if (stripeRefundId) refundDetails.stripeRefundId = stripeRefundId;
  if (refundProcessedAmount !== undefined)
    refundDetails.refundProcessedAmount = refundProcessedAmount;
  if (refundProcessedCurrency)
    refundDetails.refundProcessedCurrency = refundProcessedCurrency;
  if (refundFxRate !== undefined) refundDetails.refundFxRate = refundFxRate;

  const historyEntry = createPaymentHistoryEntry(
    "refund",
    teamMember,
    refundDetails,
    timestamp
  );

  const updatedPayment: JobPayment = {
    ...payment,
    totalRefunded: newTotalRefunded,
    history: [...(payment.history || []), historyEntry],
  };

  // Update payments array
  const updatedPayments = [...job.payments];
  updatedPayments[paymentIndex] = updatedPayment;

  // Reverse the linked processing fee in step with the refund. It used to be
  // kept ("the fee was already incurred"), but that charged the customer for a
  // transaction that was undone: a fully refunded job showed a balance HIGHER
  // than before the payment, by exactly the fee. The processor keeping its cut
  // is the merchant's cost of reversing, not the customer's. Delete already
  // removes the fee line; refund now mirrors it, proportionally.
  //
  // Partial refunds scale the fee line to the unrefunded fraction of the
  // payment, so refunding half the payment leaves half the fee. Fee tax
  // follows automatically: the job-level tax recalculation below reads the
  // line's price.
  let updatedLineItems = [...job.lineItems];
  const linkedFeeItem = findLinkedProcessingFeeLineItem(payment, job.lineItems);
  if (linkedFeeItem && payment.processingFee) {
    const paymentTotal = toDecimal(payment.total || payment.amount);
    const isFullRefund =
      paymentTotal.lessThanOrEqualTo(0) ||
      isGreaterThanOrEqual(
        newTotalRefunded,
        Number(paymentTotal.toDecimalPlaces(2))
      );

    if (isFullRefund) {
      updatedLineItems = updatedLineItems.filter(
        (item) => item.uid !== linkedFeeItem.uid
      );
    } else {
      const remainingFraction = toDecimal(1).minus(
        toDecimal(newTotalRefunded).div(paymentTotal)
      );
      const reducedFee = toDecimal(payment.processingFee)
        .times(remainingFraction)
        .toDecimalPlaces(2)
        .toNumber();
      updatedLineItems = updatedLineItems.map((item) =>
        item.uid === linkedFeeItem.uid ? { ...item, price: reducedFee } : item
      );
    }
  }

  // Recalculate job totals
  const calculations = {
    subtotal: calculateJob.subtotal(updatedLineItems).raw(),
    totalDiscount: calculateJob.totalDiscount(updatedLineItems).raw(),
    subtotalAfterDiscounts: calculateJob
      .subtotalAfterDiscounts(updatedLineItems)
      .raw(),
    totalTax: calculateJob
      .tax(updatedLineItems, job.taxRate, job.includeTax)
      .raw(),
    grandTotal: calculateJob
      .grandTotal(updatedLineItems, job.taxRate, job.includeTax)
      .raw(),
    totalDeposit: calculateJob.totalDeposit(updatedLineItems).raw(),
    remainingBalance: calculateJob
      .remainingBalance(
        updatedLineItems,
        job.taxRate,
        updatedPayments,
        job.includeTax
      )
      .raw(),
  };

  // Recalculate payment status based on updated payments
  const newPaymentStatus = calculatePaymentStatus(
    updatedPayments,
    calculations.totalDeposit,
    calculations.grandTotal,
    calculations.remainingBalance
  );

  const updatedJob: JobV2 = {
    ...job,
    ...calculations,
    payments: updatedPayments,
    lineItems: updatedLineItems,
    paymentStatus: newPaymentStatus,
  };

  return { success: true, job: updatedJob, paymentStatus: newPaymentStatus };
};

export interface PaymentActionExplanation {
  context: string;
  actions: { action: string; available: boolean; reason: string }[];
}

/**
 * Returns a user-friendly explanation of what actions are available on a payment and why
 */
export const getPaymentActionExplanation = (
  payment: JobPayment,
  job: JobV2
): PaymentActionExplanation => {
  const hasStripe = isStripePayment(payment);
  const hasQb = !!payment.qbPaymentId;
  const contractPayment = isContractPayment(payment, job);
  const status = getRefundStatus(payment);
  const hasRefunds = status !== "none";
  const fullyRefunded = status === "full";

  // Build context description
  let context: string;
  if (contractPayment) {
    context =
      "This payment is part of a payment plan and is locked to the agreed contract.";
  } else if (hasStripe && hasQb) {
    context =
      "This payment was collected through MountMonitor and is synced to QuickBooks. Because real money was captured, it cannot be edited or deleted. Refunds will be processed back to the original payment method and the QB payment will be edited down with a note.";
  } else if (hasStripe) {
    context =
      "This payment was processed directly through MountMonitor. Because real money was captured, it cannot be edited or deleted.";
  } else if (hasQb) {
    context =
      "This payment is synced to QuickBooks. Edits and deletes sync directly. Refunds will edit the QB payment amount down and add a note.";
  } else {
    context = "This payment was not processed directly through MountMonitor.";
  }

  if (fullyRefunded) {
    context += " This payment has been fully refunded.";
  } else if (hasRefunds) {
    context += " This payment has partial refunds applied.";
  }

  // Build actions list
  const editCheck = canEditPayment(payment, job);
  const deleteCheck = canDeletePayment(payment, job);
  const refundCheck = canRefundPayment(payment, job);

  const actions = [
    {
      action: "Edit",
      available: editCheck.allowed,
      reason: editCheck.allowed
        ? hasQb
          ? "You can edit this payment. Changes will sync to QuickBooks."
          : "You can edit the amount, date, notes, type, and tender."
        : editCheck.reason!,
    },
    {
      action: "Refund",
      available: refundCheck.allowed,
      reason: refundCheck.allowed
        ? hasStripe && hasQb
          ? "You can issue a full or partial refund. The refund will be processed back to the original payment method and the QB payment will be edited down with a note."
          : hasStripe
          ? "You can issue a full or partial refund. The refund will be processed back to the original payment method."
          : hasQb
          ? "You can record a full or partial refund. The QB payment will be edited down with a note."
          : "You can record a full or partial refund."
        : refundCheck.reason!,
    },
    {
      action: "Delete",
      available: deleteCheck.allowed,
      reason: deleteCheck.allowed
        ? hasQb
          ? "You can delete this payment. It will also be removed from QuickBooks. The payment record and its history will be permanently removed."
          : "You can delete this payment. The payment record and its history will be permanently removed."
        : deleteCheck.reason!,
    },
  ];

  return { context, actions };
};

/**
 * Badge colour for a payment status. Matches the palette used on the
 * job totals report so statuses read the same everywhere.
 */
export const getPaymentStatusColor = (
  paymentStatus?: PaymentStatus | null
): string => {
  switch (paymentStatus) {
    case PaymentStatus.PaidInFull:
      return "green";
    case PaymentStatus.PartiallyPaid:
    case PaymentStatus.DepositReceived:
      return "blue";
    case PaymentStatus.AwaitingDeposit:
      return "orange";
    case PaymentStatus.FailedPayment:
      return "red";
    case PaymentStatus.Unpaid:
      return "red";
    default:
      return "gray";
  }
};

/**
 * How far through payment a job is, least paid first. Used for sorting.
 */
export const getPaymentStatusRank = (
  paymentStatus?: PaymentStatus | null
): number => {
  switch (paymentStatus) {
    case PaymentStatus.FailedPayment:
      return 0;
    case PaymentStatus.Unpaid:
      return 1;
    case PaymentStatus.AwaitingDeposit:
      return 2;
    case PaymentStatus.PartiallyPaid:
    case PaymentStatus.DepositReceived:
      return 3;
    case PaymentStatus.PaidInFull:
      return 4;
    default:
      return 5;
  }
};
